import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/shared/components/error-state';
import { cn } from '@/shared/lib/utils';

import { reasonLabel, warningLabel } from '@/shared/lib/catalogue-labels';
import { useCatalogueImpact } from '../api/brands.queries';
import type { CatalogueImpact, CatalogueImpactRequest } from '../types/brand.types';

const REASON_MAX = 500;

/**
 * Confirmation for a catalogue change, showing what it will do to customers
 * before it is made.
 *
 * The impact is computed by the Admin API from the same rules the storefront
 * and checkout use, so the numbers here are what customers will actually
 * experience. A change the server would refuse (e.g. offering an item with no
 * provider) is shown as blocked and cannot be confirmed.
 *
 * Mount with a `key` per opening so the reason field starts empty.
 */
export function CatalogueChangeDialog({
  brandId,
  request,
  onOpenChange,
  title,
  description,
  confirmLabel,
  variant = 'default',
  isPending,
  countryName,
  denominationName,
  onConfirm,
}: {
  brandId: number;
  /** The change to preview; null closes the dialog. */
  request: CatalogueImpactRequest | null;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  variant?: 'default' | 'destructive';
  isPending: boolean;
  countryName: (countryId: number) => string;
  denominationName?: (denominationId: number) => string;
  onConfirm: (reason: string | null) => void;
}) {
  const [reason, setReason] = useState('');
  const impact = useCatalogueImpact(brandId, request);

  const blocked = impact.data ? impact.data.blocked.length > 0 : false;
  const canConfirm = impact.isSuccess && !blocked && !isPending && reason.length <= REASON_MAX;

  return (
    <AlertDialog open={request !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-xs leading-relaxed">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <section aria-label="Impact" aria-busy={impact.isLoading} className="space-y-3 text-xs">
          {impact.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {impact.error && (
            <ErrorState error={impact.error} onRetry={() => void impact.refetch()} />
          )}

          {impact.data && (
            <ImpactSummary
              impact={impact.data}
              countryName={countryName}
              denominationName={denominationName}
            />
          )}
        </section>

        <div className="space-y-1.5">
          <Label htmlFor="catalogue-change-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              (optional, kept in the audit log)
            </span>
          </Label>
          <Input
            id="catalogue-change-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={REASON_MAX}
            disabled={isPending}
            className="h-8 text-xs"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} className="h-8 text-xs">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              // Stay open with a spinner until the change settles.
              event.preventDefault();
              const trimmed = reason.trim();
              onConfirm(trimmed === '' ? null : trimmed);
            }}
            disabled={!canConfirm}
            className={cn(
              buttonVariants({ variant: variant === 'destructive' ? 'destructive' : 'default' }),
              'h-8 gap-1.5 text-xs',
            )}
          >
            {isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ImpactSummary({
  impact,
  countryName,
  denominationName,
}: {
  impact: CatalogueImpact;
  countryName: (countryId: number) => string;
  denominationName?: (denominationId: number) => string;
}) {
  return (
    <>
      <p>
        Applies to <strong>{impact.affectedCount}</strong> denomination
        {impact.affectedCount === 1 ? '' : 's'}.
      </p>

      {impact.countries.length > 0 && (
        <table className="w-full text-start">
          <thead className="text-muted-foreground">
            <tr>
              <th className="py-1 text-start font-medium">Country</th>
              <th className="py-1 text-end font-medium">Hidden from customers</th>
              <th className="py-1 text-end font-medium">Newly visible</th>
              <th className="py-1 text-end font-medium">Newly purchasable</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {impact.countries.map((country) => (
              <tr key={country.countryId} className="border-border border-t">
                <td className="py-1">{countryName(country.countryId)}</td>
                <td className="py-1 text-end">{country.becomeHidden}</td>
                <td className="py-1 text-end">{country.becomeVisible}</td>
                <td className="py-1 text-end">{country.becomeSellable}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {impact.blocked.length > 0 && (
        <div
          role="alert"
          className="border-status-danger-border bg-status-danger-bg text-status-danger rounded-md border p-2"
        >
          <p className="font-medium">This change cannot be made:</p>
          <ul className="mt-1 list-disc ps-4">
            {impact.blocked.map((item) => (
              <li key={item.denominationId}>
                {denominationName?.(item.denominationId) ?? `#${item.denominationId}`} —{' '}
                {reasonLabel(item.reason)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {impact.warnings.map((warning) => (
        <p key={warning} className="text-status-warning flex items-start gap-1.5">
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          {warningLabel(warning)}
        </p>
      ))}
    </>
  );
}
